// Computes the on-screen position of a character inside a <textarea>, using the
// classic hidden-mirror-div technique (a textarea exposes no caret coordinates).

export interface CaretRect {
  top: number;
  left: number;
  height: number;
}

// Style properties that affect text layout — copied onto the mirror so it wraps
// text exactly like the textarea. Width and box-sizing are handled separately.
const mirroredStyles = [
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
] as const;

// Viewport-relative position of the character at `index` within the textarea.
export function caretViewportRect(textarea: HTMLTextAreaElement, index: number): CaretRect {
  const doc = textarea.ownerDocument;
  const computed = getComputedStyle(textarea);

  const mirror = doc.createElement('div');
  const style = mirror.style;
  style.position = 'absolute';
  style.top = '0';
  style.left = '0';
  style.visibility = 'hidden';
  style.overflow = 'hidden';
  style.whiteSpace = 'pre-wrap';
  style.wordWrap = 'break-word';
  // getComputedStyle().width is the content-box width, so mirror in content-box.
  style.boxSizing = 'content-box';
  style.width = computed.width;
  for (const prop of mirroredStyles) {
    style[prop] = computed[prop];
  }

  mirror.textContent = textarea.value.slice(0, index);
  const marker = doc.createElement('span');
  // Needs non-empty content to have a measurable box.
  marker.textContent = textarea.value.slice(index) || '.';
  mirror.appendChild(marker);

  doc.body.appendChild(mirror);
  const markerTop = marker.offsetTop;
  const markerLeft = marker.offsetLeft;
  doc.body.removeChild(mirror);

  const rect = textarea.getBoundingClientRect();
  const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2;
  return {
    top: rect.top + markerTop - textarea.scrollTop,
    left: rect.left + markerLeft - textarea.scrollLeft,
    height: lineHeight,
  };
}
